import React, { useEffect, useState } from 'react';
import { CrudStyles } from '../shared/Crud';
import { RegistrationStatusBadge } from '../CustomerSelector/styles';
import { TabBar, TabButton, TabContent } from '../Deliveries/DeliveriesList.styles';
import { CostCenterListPanel } from '../CostCenters';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import legalPersonService from '../../services/legalPersonService';
import addressService from '../../services/addressService';
import { formatDateTimeBR } from '../../utils/dateTimeDisplay';
import { formatDateBR, maskCNPJ, maskCPF, maskPhone } from '../../utils/masks';
import { mapAddressFromApi } from '../../utils/mapAddressFromApi';
import ReadOnlyAddressFields from '../shared/ReadOnlyAddressFields';
import { getRegistrationStatusDescription, getRegistrationStatusLabel } from '../../utils/registrationStatus';

const { Container, Header, Button, SectionTitle, FormRow, FormGroup } = CrudStyles;

const valueStyle = {
    margin: 0,
    fontSize: 15,
    color: '#1a1a1a',
    lineHeight: 1.5,
    wordBreak: 'break-word',
};

const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
};

const sectionCard = {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '20px 20px 12px',
};

const emptyStateBox = {
    margin: 0,
    padding: '16px 18px',
    background: '#fff',
    border: '1px dashed #cbd5e1',
    borderRadius: 8,
    color: '#64748b',
    fontSize: 14,
    lineHeight: 1.5,
};

const loadingBox = {
    padding: '28px 20px',
    textAlign: 'center',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    color: '#64748b',
    fontSize: 14,
};

function ReadOnlyField({ label, children }) {
    return (
        <FormGroup>
            <span style={labelStyle}>{label}</span>
            <div style={valueStyle}>{children}</div>
        </FormGroup>
    );
}

function formatPhoneList(phoneNumbers) {
    if (!Array.isArray(phoneNumbers) || phoneNumbers.length === 0) {
        return '—';
    }
    const parts = phoneNumbers
        .map((p) => (typeof p === 'string' ? p : p?.number))
        .filter(Boolean)
        .map((n) => {
            const digits = String(n).replace(/\D/g, '').slice(0, 11);
            return digits.length ? maskPhone(digits) : null;
        })
        .filter(Boolean);
    return parts.length ? parts.join(' · ') : '—';
}

function registrationStatusValue(dto) {
    return dto?.registrationStatus ?? dto?.status ?? null;
}

/**
 * Consulta detalhada de pessoa jurídica (somente visualização).
 */
export default function LegalPersonDetailView({ legalPersonId, onBack }) {
    const { selectedCustomerId } = useCustomerSelector();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [detailTab, setDetailTab] = useState('dados');
    const [reloadTick, setReloadTick] = useState(0);
    const [responsibleAddress, setResponsibleAddress] = useState(null);

    useEffect(() => {
        setDetailTab('dados');
    }, [legalPersonId, reloadTick]);

    useEffect(() => {
        if (legalPersonId == null) {
            return;
        }
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const dto = await legalPersonService.getById(legalPersonId);
                if (!cancelled) {
                    setData(dto);
                }
            } catch {
                if (!cancelled) {
                    setError('Não foi possível carregar os dados desta pessoa jurídica.');
                    setData(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [legalPersonId, reloadTick]);

    useEffect(() => {
        setResponsibleAddress(null);
        const addressId = data?.responsible?.addressId;
        const customerId = data?.responsible?.customerId ?? selectedCustomerId;
        if (!addressId || customerId == null) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const addrDto = await addressService.getById(addressId, customerId);
                if (!cancelled) setResponsibleAddress(mapAddressFromApi(addrDto));
            } catch {
                if (!cancelled) setResponsibleAddress(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [data?.responsible?.addressId, data?.responsible?.customerId, selectedCustomerId, reloadTick]);

    const regStatus = data ? registrationStatusValue(data) : null;
    const regDesc = getRegistrationStatusDescription(regStatus);

    const headerSubtitle =
        data && (data.fantasyName || data.name)
            ? data.fantasyName || data.name
            : null;

    return (
        <Container>
            <Header>
                <div style={{ minWidth: 0, flex: '1 1 12rem' }}>
                    <h1>Detalhes da pessoa jurídica</h1>
                    {!loading && headerSubtitle && (
                        <p
                            style={{
                                margin: '6px 0 0',
                                fontSize: 14,
                                fontWeight: 500,
                                color: '#64748b',
                                lineHeight: 1.4,
                            }}
                        >
                            {headerSubtitle}
                        </p>
                    )}
                </div>
                <Button type="button" variant="outline" onClick={onBack}>
                    Voltar
                </Button>
            </Header>

            {loading && (
                <div style={loadingBox} role="status" aria-live="polite">
                    Carregando dados…
                </div>
            )}
            {error && (
                <div
                    style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        padding: '14px 16px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: 10,
                    }}
                    role="alert"
                >
                    <p style={{ color: '#b91c1c', margin: 0, fontSize: 14 }}>{error}</p>
                    <Button type="button" variant="outline" onClick={() => setReloadTick((v) => v + 1)}>
                        Tentar novamente
                    </Button>
                </div>
            )}

            {!loading && !error && data && (
                <>
                    <TabBar aria-label="Seções do cadastro">
                        <TabButton
                            type="button"
                            $active={detailTab === 'dados'}
                            onClick={() => setDetailTab('dados')}
                        >
                            Dados gerais
                        </TabButton>
                        <TabButton
                            type="button"
                            $active={detailTab === 'centros'}
                            onClick={() => setDetailTab('centros')}
                        >
                            Centros de custo
                        </TabButton>
                    </TabBar>
                    <TabContent style={{ paddingTop: 4 }}>
                        {detailTab === 'dados' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                <div style={sectionCard}>
                                    <SectionTitle>Dados da empresa</SectionTitle>
                                    <FormRow>
                                        <ReadOnlyField label="ID">{data.id != null ? String(data.id) : '—'}</ReadOnlyField>
                                        <ReadOnlyField label="CNPJ">
                                            {data.document ? maskCNPJ(String(data.document)) : '—'}
                                        </ReadOnlyField>
                                    </FormRow>
                                    <FormRow>
                                        <ReadOnlyField label="Razão social">{data.name || '—'}</ReadOnlyField>
                                        <ReadOnlyField label="Nome fantasia">{data.fantasyName || '—'}</ReadOnlyField>
                                    </FormRow>
                                    <FormRow>
                                        <ReadOnlyField label="Situação cadastral">
                                            <RegistrationStatusBadge $status={regStatus} title={regDesc || undefined}>
                                                {getRegistrationStatusLabel(regStatus)}
                                            </RegistrationStatusBadge>
                                        </ReadOnlyField>
                                    </FormRow>
                                </div>

                                <div style={sectionCard}>
                                    <SectionTitle>Auditoria</SectionTitle>
                                    <FormRow>
                                        <ReadOnlyField label="E-mail do cadastrante">{data.creatorEmail || '—'}</ReadOnlyField>
                                        <ReadOnlyField label="Última atualização por">{data.lastUpdateBy || '—'}</ReadOnlyField>
                                    </FormRow>
                                    <FormRow>
                                        <ReadOnlyField label="Criado em">{formatDateTimeBR(data.creationDateTime)}</ReadOnlyField>
                                        <ReadOnlyField label="Atualizado em">{formatDateTimeBR(data.lastUpdateDateTime)}</ReadOnlyField>
                                    </FormRow>
                                </div>

                                <div style={sectionCard}>
                                    <SectionTitle>Responsável</SectionTitle>
                                    {!data.responsible ? (
                                        <p style={emptyStateBox}>
                                            Nenhum responsável vinculado a este cadastro.
                                        </p>
                                    ) : (
                                        <>
                                            <FormRow>
                                                <ReadOnlyField label="Nome">{data.responsible.name || '—'}</ReadOnlyField>
                                                <ReadOnlyField label="E-mail">{data.responsible.email || '—'}</ReadOnlyField>
                                            </FormRow>
                                            <FormRow>
                                                <ReadOnlyField label="CPF">
                                                    {data.responsible.cpf
                                                        ? maskCPF(String(data.responsible.cpf).replace(/\D/g, ''))
                                                        : '—'}
                                                </ReadOnlyField>
                                                <ReadOnlyField label="Data de nascimento">
                                                    {data.responsible.birthDate
                                                        ? formatDateBR(
                                                              String(data.responsible.birthDate).length >= 10
                                                                  ? String(data.responsible.birthDate).slice(0, 10)
                                                                  : ''
                                                          ) || String(data.responsible.birthDate)
                                                        : '—'}
                                                </ReadOnlyField>
                                            </FormRow>
                                            <FormRow>
                                                <ReadOnlyField label="Telefones">
                                                    {formatPhoneList(data.responsible.phoneNumbers)}
                                                </ReadOnlyField>
                                                <ReadOnlyField label="Cargo">{data.responsible.jobTitle || '—'}</ReadOnlyField>
                                            </FormRow>
                                            <FormRow>
                                                <ReadOnlyField label="ID do colaborador">
                                                    {data.responsible.id != null ? String(data.responsible.id) : '—'}
                                                </ReadOnlyField>
                                            </FormRow>
                                            <FormRow>
                                                <ReadOnlyField label="Ativo na plataforma">
                                                    {data.responsible.active ? 'Sim' : 'Não'}
                                                </ReadOnlyField>
                                                <ReadOnlyField label="Cadastro removido">
                                                    {data.responsible.removed ? 'Sim' : 'Não'}
                                                </ReadOnlyField>
                                            </FormRow>
                                            <FormRow>
                                                <ReadOnlyField label="ID do cliente">
                                                    {data.responsible.customerId != null ? String(data.responsible.customerId) : '—'}
                                                </ReadOnlyField>
                                            </FormRow>
                                        </>
                                    )}
                                </div>

                                {data.responsible && (
                                    <div style={sectionCard}>
                                        <ReadOnlyAddressFields
                                            address={responsibleAddress}
                                            title="Endereço do responsável"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        {detailTab === 'centros' && (
                            <CostCenterListPanel
                                customerId={data.id}
                                idPrefix={`pj-detail-${data.id}-cc`}
                                title="Centros de custo"
                            />
                        )}
                    </TabContent>
                </>
            )}
        </Container>
    );
}
